import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';

const navItems = [
  { path: '/app', label: 'Dashboard', icon: DashboardIcon },
  { path: '/app/children', label: 'Children', icon: ChildrenIcon, priority: true },
  { path: '/app/inbox', label: 'Inbox', icon: InboxIcon },
  { path: '/app/finances', label: 'Finances', icon: FinancesIcon },
  { path: '/app/notifications', label: 'Alerts', icon: NotificationsIcon },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { preferences, toggleFocusMode } = usePreferences();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-surface-light dark:bg-surface-dark safe-top safe-bottom">
      {/* Mobile Header */}
      <header className="sticky top-0 z-30 bg-white/90 dark:bg-surface-darker/90 backdrop-blur-sm border-b border-neutral-200 dark:border-neutral-800 lg:hidden">
        <div className="flex items-center justify-between px-4 h-14">
          <h1 className="text-xl font-bold text-primary-600 dark:text-primary-400">WEZ</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleFocusMode}
              className={`p-2 rounded-soft transition-colors ${
                preferences.focusMode
                  ? 'bg-calm-100 text-calm-700 dark:bg-calm-900/30 dark:text-calm-300'
                  : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800'
              }`}
              aria-label={preferences.focusMode ? 'Exit focus mode' : 'Enter focus mode'}
            >
              <FocusIcon />
            </button>
            <NavLink to="/app/settings" className="p-2 rounded-soft hover:bg-neutral-100 dark:hover:bg-neutral-800">
              <SettingsIcon />
            </NavLink>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white dark:bg-surface-darker border-r border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center h-16 px-6 border-b border-neutral-200 dark:border-neutral-800">
            <h1 className="text-2xl font-bold text-primary-600 dark:text-primary-400">WEZ</h1>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `${isActive ? 'nav-item-active' : 'nav-item'} ${item.priority ? 'child-related' : ''}`
                }
              >
                <item.icon />
                <span>{item.label}</span>
                {item.priority && (
                  <span className="ml-auto badge-warmth text-xs">Priority</span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="p-4 border-t border-neutral-200 dark:border-neutral-800">
            <div className="flex items-center gap-3 mb-4">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 font-medium">
                    {user?.name?.charAt(0) || 'W'}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={toggleFocusMode}
                className={`flex-1 btn ${
                  preferences.focusMode ? 'btn-warmth' : 'btn-ghost'
                } text-sm py-2`}
              >
                <FocusIcon />
                Focus
              </button>
              <NavLink to="/app/settings" className="btn btn-ghost text-sm py-2">
                <SettingsIcon />
              </NavLink>
            </div>

            <button
              onClick={logout}
              className="w-full mt-2 btn btn-ghost text-sm py-2 text-neutral-500"
            >
              Sign out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64">
          <div className="p-4 lg:p-6 max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white/90 dark:bg-surface-darker/90 backdrop-blur-sm border-t border-neutral-200 dark:border-neutral-800 safe-bottom lg:hidden">
        <div className="flex justify-around items-center h-16 px-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center min-w-[4rem] py-2 rounded-soft transition-colors ${
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-neutral-500 dark:text-neutral-400'
                } ${item.priority && isActive ? 'bg-warmth-100 dark:bg-warmth-900/30' : ''}`}
              >
                <item.icon />
                <span className="text-xs mt-1 font-medium">{item.label}</span>
                {item.priority && !isActive && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-warmth-500 rounded-full" />
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Spacer for mobile nav */}
      <div className="h-16 lg:hidden" />
    </div>
  );
}

// Icon components
function DashboardIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  );
}

function ChildrenIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
    </svg>
  );
}

function FinancesIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function NotificationsIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function FocusIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}
